/**
 * プレビュー描画用の「表示される列」モデル。
 *
 * 列は子に条件分岐を持ち、条件分岐は分岐質問を持つ。分岐質問も同じ規則で条件分岐を持てる
 * (深さは TABLE_LOGIC_MAX_DEPTH まで)。
 * このツリーを深さ優先で、ヘッダーに左から並ぶ順へ平坦化したものが表示される列。
 *
 *   テンプレート行
 *   ├─ 列0
 *   ├─ 列1
 *   │   └─ 条件分岐("はい を選択した場合")
 *   │       ├─ 分岐質問0
 *   │       │   └─ 条件分岐("有 を選択した場合")
 *   │       │       └─ 分岐質問0-0
 *   │       └─ 分岐質問1
 *   └─ 列2
 *   → [列0, 列1, 分岐質問0, 分岐質問0-0, 分岐質問1, 列2]
 *
 * 分岐質問は根から順の条件(conditions)を持ち、ヘッダーはそれを深さごとにまとめて帯に描く。
 * 並び替えの単位は列(columnIndex)で、分岐質問は列の子として一緒に動く。
 */

import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getLogicConditionLabel } from './logicConditionLabel'
import {
  getLogicBranchQuestions,
  getQuestionLogicNodes,
  getTableColumnNodes,
} from './selector'

/** 列に付けられる条件分岐の最大の深さ。列の直下の条件分岐が深さ 1。 */
export const TABLE_LOGIC_MAX_DEPTH = 3

/** 分岐質問が持つ条件1つ。条件を表示する帯1本に対応する。 */
export type TableColumnCondition = {
  logicId: number
  label: string
}

/** 列そのものを表す表示される列。 */
export type TableQuestionColumn = {
  kind: 'column'
  /** React key。 */
  key: string
  /** 並び替えの単位となる列の index。 */
  columnIndex: number
  /** 所属列の質問ノード id。 */
  columnId: number
  /** 列の質問ノード。 */
  question: TemplateNodeSchema
}

/** 条件分岐の分岐質問を表す表示される列。 */
export type TableBranchColumn = {
  kind: 'branch'
  key: string
  /** この分岐質問が属する列の index(並び替え単位)。 */
  columnIndex: number
  columnId: number
  /** 直近の条件分岐の id。 */
  logicId: number
  /** 直近の条件分岐を持つ質問の ID。 */
  parentQuestionId: number
  /** 根から順の条件。最後の要素が直近の条件分岐で、要素数がこの列の深さ。 */
  conditions: TableColumnCondition[]
  /** 分岐質問ノード。分岐質問を持たない条件分岐では undefined。 */
  question: TemplateNodeSchema | undefined
}

export type TableDisplayColumn = TableQuestionColumn | TableBranchColumn

/**
 * 表を「左から並ぶ表示される列」へ平坦化する。
 * 分岐質問が未追加の条件分岐は、条件を表示する帯だけを出すために question が
 * undefined の列を1つ生成する。
 */
export const getTableDisplayColumns = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TableDisplayColumn[] => {
  const columns = getTableColumnNodes(tableNode, templateNodes)

  return columns.flatMap((columnNode, columnIndex): TableDisplayColumn[] => {
    const column: TableQuestionColumn = {
      kind: 'column',
      key: `column-${columnNode.id}`,
      columnIndex,
      columnId: columnNode.id,
      question: columnNode,
    }

    const flattenBranches = (
      parentQuestion: TemplateNodeSchema,
      ancestorConditions: TableColumnCondition[],
    ): TableBranchColumn[] =>
      getQuestionLogicNodes(parentQuestion, templateNodes).flatMap(
        logicNode => {
          const conditions = [
            ...ancestorConditions,
            { logicId: logicNode.id, label: getLogicConditionLabel(logicNode) },
          ]
          const shared = {
            kind: 'branch' as const,
            columnIndex,
            columnId: columnNode.id,
            logicId: logicNode.id,
            parentQuestionId: parentQuestion.id,
            conditions,
          }
          const branches = getLogicBranchQuestions(logicNode, templateNodes)

          if (branches.length === 0) {
            return [
              {
                ...shared,
                key: `branch-empty-${logicNode.id}`,
                question: undefined,
              },
            ]
          }

          return branches.flatMap(branch => [
            { ...shared, key: `branch-${branch.id}`, question: branch },
            ...flattenBranches(branch, conditions),
          ])
        },
      )

    return [column, ...flattenBranches(columnNode, [])]
  })
}
